<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { getFrameSrc, getUrlFromList } from '@/helpers/videoUrlUtils'

const props = defineProps({
  review: {
    type: Object as () => {
      url?: string
      type?: string
      photo?: string
      author_name?: string
      date?: string
      text?: string
    } | null,
    default: null
  }
})

const emit = defineEmits<{
  'update:review': [value: null]
}>()

const preloader = ref(true)

const frameSrc = computed(() => {
  if (!props.review) return null

  return getFrameSrc(getUrlFromList(props.review?.url ?? ''))
})

watch(frameSrc, () => {
  preloader.value = true
})
</script>

<template>
  <v-rich-modal title="Отзыв" :model-value="review !== null" @update:model-value="() => emit('update:review', null)">
    <article v-if="review" class="public-review-modal" :class="`public-review-modal_type_${review.type}`">
      <div v-if="review.type === 'v'" class="public-review-modal__video">
        <iframe
          :src="frameSrc ?? undefined"
          width="640"
          height="360"
          allowfullscreen
          @load="
            () => {
              preloader = false
            }
          "
        />

        <div v-if="preloader" class="public-review-modal__preloader">
          <v-icon class="mdi-spin" size="36">mdi-timer-sand</v-icon>
        </div>
      </div>

      <div
        v-if="review.type === 't'"
        class="public-review-modal__photo"
        :style="{ backgroundImage: `url(${review.photo})` }"
      >
        <div class="public-review-modal__photo_overlay" aria-hidden>
          <v-icon size="40">mdi-play</v-icon>
          <span class="ml-1">Посмотреть отзыв</span>
        </div>
      </div>

      <p class="public-review-modal__name text-body-bold" v-text="review.author_name" />

      <p class="public-review-modal__date" v-text="review.date" />

      <p class="public-review-modal__text" v-text="review.text" />

      <v-link
        v-if="review.type === 't' && review.url"
        mode="primary"
        class="public-review-modal__link"
        :href="review.url"
      >
        Подробнее
      </v-link>
    </article>
  </v-rich-modal>
</template>

<style lang="scss">
.public-review-modal {
  &_type {
    &_t {
      max-width: min(640px, 100vw);

      display: grid;
      grid-template-columns: auto 1fr auto;
      grid-template-rows: auto 1fr auto;
      gap: 16px 16px;
      grid-template-areas:
        'photo name date'
        'text text text'
        'link link link';
    }

    &_v {
      @include desktop {
        max-width: min(90vw, 960px);
      }

      @include mobile {
        max-width: 100%;
      }

      display: grid;
      grid-template-columns: 1fr auto;
      grid-template-rows: 1fr auto auto;
      gap: 16px 16px;
      grid-template-areas:
        'video video'
        'name date'
        'text text';
    }
  }

  &__photo {
    grid-area: photo;

    background-size: cover;
    background-position: center;

    width: 48px;
    height: 48px;
    border-radius: 999px;
  }

  &__video {
    grid-area: video;

    position: relative;
    overflow: hidden;
    width: 100%;
    padding-top: 56.25%; /* 16:9 Aspect Ratio (divide 9 by 16 = 0.5625) */
    height: 0;

    @include desktop {
      width: 640px;
    }

    & > iframe {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
    }
  }

  &__video > &__preloader {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;

    background: black;
    color: white;

    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__name {
    grid-area: name;
    align-self: center;
    margin: 0;
  }

  &__date {
    grid-area: date;
    align-self: center;
    margin: 0;

    font-size: 14px;
    font-weight: 400;
    line-height: 24px;

    color: $darkGray;
  }

  &__text {
    grid-area: text;
    margin: 0;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }

  &__link {
    grid-area: link;
    justify-self: end;
  }
}
</style>
